import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { X, Check, ArrowRight } from "lucide-react";
import dashboardImg from "@/assets/dashboard-screenshot.png";
import membersImg from "@/assets/members-screenshot.png";
import eventsImg from "@/assets/events-screenshot.png";

const LandingOption5 = () => {
  return (
    <div className="min-h-screen">
      {/* Problem Section */}
      <section className="relative bg-background py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center space-y-6 animate-fade-in">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">
              Managing Your PTA Shouldn't Feel Like
              <span className="block text-destructive mt-2">A Full-Time Job</span>
            </h1>
            <p className="text-xl text-muted-foreground">
              Hours spent on spreadsheets, endless email chains, and juggling multiple tools
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <Card className="border-destructive/20">
              <CardHeader>
                <div className="flex items-start gap-3">
                  <X className="h-6 w-6 text-destructive shrink-0 mt-1" />
                  <div>
                    <CardTitle className="text-lg">Time-Consuming Admin</CardTitle>
                    <CardDescription className="mt-2">
                      Spending 10+ hours weekly on manual tasks
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>
            <Card className="border-destructive/20">
              <CardHeader>
                <div className="flex items-start gap-3">
                  <X className="h-6 w-6 text-destructive shrink-0 mt-1" />
                  <div>
                    <CardTitle className="text-lg">Scattered Information</CardTitle>
                    <CardDescription className="mt-2">
                      Data spread across multiple platforms
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>
            <Card className="border-destructive/20">
              <CardHeader>
                <div className="flex items-start gap-3">
                  <X className="h-6 w-6 text-destructive shrink-0 mt-1" />
                  <div>
                    <CardTitle className="text-lg">Low Engagement</CardTitle>
                    <CardDescription className="mt-2">
                      Struggling to get parents involved
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-24 px-4 bg-gradient-hero">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center space-y-6 mb-16">
            <h2 className="text-5xl md:text-6xl font-bold text-foreground">
              Introducing
              <span className="block bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent mt-2">
                PTA Hub
              </span>
            </h2>
            <p className="text-2xl text-muted-foreground max-w-3xl mx-auto">
              The all-in-one platform that transforms PTA management from overwhelming to effortless
            </p>
          </div>

          <Card className="overflow-hidden shadow-elevated">
            <img src={dashboardImg} alt="PTA Hub Dashboard" className="w-full" />
          </Card>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground">
              How PTA Hub Solves Your Challenges
            </h2>
          </div>

          <div className="space-y-16">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Card className="overflow-hidden shadow-elevated">
                  <img src={membersImg} alt="Member Management" className="w-full" />
                </Card>
              </div>
              <div className="space-y-6">
                <div className="inline-block px-4 py-2 bg-primary/10 rounded-full">
                  <span className="text-primary font-semibold">Save 10+ Hours Weekly</span>
                </div>
                <h3 className="text-3xl font-bold text-foreground">
                  Automate Your Admin Tasks
                </h3>
                <p className="text-lg text-muted-foreground">
                  Stop wasting time on spreadsheets and manual processes. PTA Hub automates member management, event coordination, and financial tracking.
                </p>
                <ul className="space-y-3">
                  {[
                    "Automated member invitations and onboarding",
                    "One-click event creation and management",
                    "Real-time financial tracking and reports"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="h-6 w-6 text-primary shrink-0 mt-1" />
                      <span className="text-lg">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6 order-2 lg:order-1">
                <div className="inline-block px-4 py-2 bg-primary/10 rounded-full">
                  <span className="text-primary font-semibold">Everything in One Place</span>
                </div>
                <h3 className="text-3xl font-bold text-foreground">
                  Centralize Your Information
                </h3>
                <p className="text-lg text-muted-foreground">
                  No more jumping between tools. All your PTA data, communications, and documents in one secure platform.
                </p>
                <ul className="space-y-3">
                  {[
                    "Single dashboard for all activities",
                    "Integrated communication tools",
                    "Secure document storage and sharing"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="h-6 w-6 text-primary shrink-0 mt-1" />
                      <span className="text-lg">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="order-1 lg:order-2">
                <Card className="overflow-hidden shadow-elevated">
                  <img src={eventsImg} alt="Event Management" className="w-full" />
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-24 px-4 bg-card/30">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground">
              The Results Speak for Themselves
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center p-8 shadow-card border-2 border-primary/20">
              <div className="text-6xl font-bold text-primary mb-3">50%</div>
              <div className="text-xl font-semibold mb-2">Increase in Engagement</div>
              <p className="text-muted-foreground">More parents participating in PTA activities</p>
            </Card>
            <Card className="text-center p-8 shadow-card border-2 border-primary/20">
              <div className="text-6xl font-bold text-primary mb-3">10+</div>
              <div className="text-xl font-semibold mb-2">Hours Saved</div>
              <p className="text-muted-foreground">Every week on administrative tasks</p>
            </Card>
            <Card className="text-center p-8 shadow-card border-2 border-primary/20">
              <div className="text-6xl font-bold text-primary mb-3">500+</div>
              <div className="text-xl font-semibold mb-2">PTAs Trust Us</div>
              <p className="text-muted-foreground">Schools across the UK using PTA Hub</p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 bg-gradient-hero">
        <div className="container mx-auto max-w-4xl text-center space-y-8">
          <h2 className="text-5xl md:text-6xl font-bold text-foreground">
            Transform Your PTA Management Today
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Join hundreds of PTAs saving time and increasing engagement with PTA Hub
          </p>
          <div className="space-y-4">
            <Link to="/auth">
              <Button size="lg" className="shadow-elevated text-xl px-14 py-8">
                Start Your Free 14-Day Trial
                <ArrowRight className="ml-2 h-6 w-6" />
              </Button>
            </Link>
            <p className="text-sm text-muted-foreground">
              No credit card required • Full access • Cancel anytime
            </p>
          </div>
        </div>
      </section>

      {/* Label */}
      <div className="fixed top-20 right-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg shadow-lg font-bold z-50">
        Option 5
      </div>
    </div>
  );
};

export default LandingOption5;
